"use client"

/**
 * useGpuEffect — the single entry point for every GPU-backed animation.
 *
 * An effect author implements four functions — setup, frame, resize, teardown —
 * and gets, for free and correct-by-construction:
 *   - SSR safety and a reduced-motion / unsupported-browser static fallback
 *   - the shared, long-lived WebGPU device (or a WebGL2 context, or a managed
 *     canvas for a bring-your-own-renderer effect like three.js)
 *   - a healthy canvas surface that survives scroll-away + resize (WebGPU)
 *   - one unified frame loop with work/present gating
 *   - guaranteed teardown (resources released, context lost, subscription cancelled)
 *   - device-lost recovery (rebuild against a fresh device)
 *
 * Backends:
 *   - 'webgpu' : the platform owns the device + swap-chain surface.
 *   - 'webgl2' : the platform hands you a WebGL2 context and releases it.
 *   - 'canvas' : the platform hands you a bare <canvas> and its lifecycle; you
 *                bring the renderer (three.js, raw WebGL1) and release it in
 *                teardown. Sizes are reported in CSS pixels; you own the backing
 *                store (call renderer.setSize / set canvas.width yourself).
 *
 * mode:
 *   - 'pauseWork' (WebGPU default): the loop keeps ticking off-screen but skips
 *     the effect's frame, doing a cheap keep-alive present so the swap-chain
 *     surface never goes stale — this is what prevents the device-lost crash.
 *   - 'pauseAll'  (WebGL2 / canvas default): the scheduler skips the effect
 *     entirely off-screen (safe for WebGL, which survives an idle surface).
 */

import * as React from "react"
import { useEffect, useRef, useState } from "react"

import { frameScheduler } from "@/core/utils/frame-scheduler"
import { shouldRenderStatic } from "./capabilities"
import { acquireDevice, getPreferredFormat, onDeviceLost } from "./webgpu-device"
import { createWebGPUSurface, type WebGPUSurface } from "./surface"

type Backend = "webgpu" | "webgl2" | "canvas"
type Mode = "pauseWork" | "pauseAll"

export interface WebGPUSetupContext {
  backend: "webgpu"
  device: GPUDevice
  format: GPUTextureFormat
  surface: WebGPUSurface
  canvas: HTMLCanvasElement
  width: number
  height: number
}
export interface WebGL2SetupContext {
  backend: "webgl2"
  gl: WebGL2RenderingContext
  canvas: HTMLCanvasElement
  width: number
  height: number
}
export interface CanvasSetupContext {
  backend: "canvas"
  canvas: HTMLCanvasElement
  /** CSS pixels — you own the backing store. */
  width: number
  height: number
}
export type GpuSetupContext = WebGPUSetupContext | WebGL2SetupContext | CanvasSetupContext

export interface WebGPUFrameContext {
  backend: "webgpu"
  device: GPUDevice
  surface: WebGPUSurface
  currentTexture: GPUTexture
  width: number
  height: number
  onScreen: boolean
  now: number
}
export interface WebGL2FrameContext {
  backend: "webgl2"
  gl: WebGL2RenderingContext
  width: number
  height: number
  onScreen: boolean
  now: number
}
export interface CanvasFrameContext {
  backend: "canvas"
  canvas: HTMLCanvasElement
  /** CSS pixels. */
  width: number
  height: number
  onScreen: boolean
  now: number
}
export type GpuFrameContext = WebGPUFrameContext | WebGL2FrameContext | CanvasFrameContext

export interface GpuEffectConfig<S> {
  backend: Backend
  /** Off-screen behaviour. Defaults to pauseWork (webgpu) / pauseAll (else). */
  mode?: Mode
  id?: string
  priority?: "critical" | "normal" | "idle"
  className?: string
  style?: React.CSSProperties
  /** Decoration rendered above the canvas (vignettes, gradients). */
  overlay?: React.ReactNode
  /** Shown on reduced-motion, data-saver, unsupported browser, or hard failure. */
  fallback?: React.ReactNode
  alphaMode?: GPUCanvasAlphaMode
  setup: (ctx: GpuSetupContext) => S | Promise<S>
  frame: (state: S, ctx: GpuFrameContext, dt: number) => void
  resize?: (state: S, width: number, height: number) => void
  teardown?: (state: S) => void
}

let uid = 0

/** Frames between keep-alive presents while parked off-screen (~4 fps). */
const KEEPALIVE_INTERVAL = 15

export function useGpuEffect<S>(config: GpuEffectConfig<S>): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const configRef = useRef(config)
  configRef.current = config

  const [failed, setFailed] = useState(false)
  // Must start false so the server and the first client render agree (SSR has no
  // GPU, so deciding the fallback here would cause a hydration mismatch). The
  // capability check runs post-mount, below, and switches to the fallback then.
  const [staticFallback, setStaticFallback] = useState(false)

  useEffect(() => {
    if (shouldRenderStatic()) {
      setStaticFallback(true)
      return
    }
    const cfg = configRef.current
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    let destroyed = false
    let state: S | null = null
    let unsubscribe: (() => void) | null = null
    let unsubscribeLost: (() => void) | null = null
    let onScreen = true
    let tick = 0
    let resizeTimer: ReturnType<typeof setTimeout> | null = null
    // CSS-pixel size, tracked for the 'canvas' backend (effect owns the backing).
    let cssW = 0
    let cssH = 0

    const mode: Mode = cfg.mode ?? (cfg.backend === "webgpu" ? "pauseWork" : "pauseAll")
    const id = cfg.id ?? `gpu-effect-${++uid}`

    let device: GPUDevice | null = null
    let surface: WebGPUSurface | null = null
    let gl: WebGL2RenderingContext | null = null

    const measure = () => {
      const r = container.getBoundingClientRect()
      return {
        w: Math.max(1, r.width || window.innerWidth),
        h: Math.max(1, r.height || window.innerHeight),
      }
    }

    const teardownState = () => {
      if (state != null) {
        try {
          cfg.teardown?.(state)
        } catch (err) {
          console.error(`[useGpuEffect] teardown '${id}' threw:`, err)
        }
        state = null
      }
    }

    const frameCallback = (now: number, dt: number) => {
      if (destroyed || state == null) return

      // pauseWork (WebGPU): keep the surface warm without the expensive frame.
      if (!onScreen) {
        if (surface && tick % KEEPALIVE_INTERVAL === 0) {
          try {
            surface.keepAlive()
          } catch {
            /* transiently unavailable; next tick retries */
          }
        }
        tick++
        return
      }

      try {
        if (cfg.backend === "webgpu" && device && surface) {
          cfg.frame(
            state,
            {
              backend: "webgpu",
              device,
              surface,
              currentTexture: surface.getCurrentTexture(),
              width: surface.width,
              height: surface.height,
              onScreen,
              now,
            },
            dt
          )
        } else if (cfg.backend === "webgl2" && gl) {
          cfg.frame(
            state,
            { backend: "webgl2", gl, width: canvas.width, height: canvas.height, onScreen, now },
            dt
          )
        } else if (cfg.backend === "canvas") {
          cfg.frame(
            state,
            { backend: "canvas", canvas, width: cssW, height: cssH, onScreen, now },
            dt
          )
        }
      } catch (err) {
        console.error(`[useGpuEffect] frame '${id}' threw:`, err)
      }
      tick++
    }

    const doResize = () => {
      const { w, h } = measure()
      const dpr = window.devicePixelRatio || 1
      let changed = false

      if (cfg.backend === "webgpu" && surface) {
        changed = surface.resize(w, h, dpr)
      } else if (cfg.backend === "webgl2" && gl) {
        const scale = Math.min(dpr, 2)
        const pw = Math.max(1, Math.round(w * scale))
        const ph = Math.max(1, Math.round(h * scale))
        if (pw !== canvas.width || ph !== canvas.height) {
          canvas.width = pw
          canvas.height = ph
          gl.viewport(0, 0, pw, ph)
          changed = true
        }
      } else if (cfg.backend === "canvas") {
        changed = w !== cssW || h !== cssH
        cssW = w
        cssH = h
      }

      if (changed && state != null) {
        try {
          cfg.resize?.(state, cfg.backend === "canvas" ? cssW : canvas.width, cfg.backend === "canvas" ? cssH : canvas.height)
        } catch (err) {
          console.error(`[useGpuEffect] resize '${id}' threw:`, err)
        }
        // A WebGPU resize while parked must not leave a stale surface.
        if (surface) {
          try {
            surface.keepAlive()
          } catch {
            /* ignore */
          }
        }
      }
    }

    const init = async () => {
      const { w, h } = measure()
      cssW = w
      cssH = h

      if (cfg.backend === "webgpu") {
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        canvas.width = Math.max(1, Math.round(w * dpr))
        canvas.height = Math.max(1, Math.round(h * dpr))
        device = await acquireDevice()
        if (destroyed) return
        if (!device) return void setFailed(true)
        const format = getPreferredFormat()
        surface = createWebGPUSurface(canvas, device, format, cfg.alphaMode)
        if (!surface) return void setFailed(true)
        state = await Promise.resolve(
          cfg.setup({ backend: "webgpu", device, format, surface, canvas, width: canvas.width, height: canvas.height })
        )
      } else if (cfg.backend === "webgl2") {
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        canvas.width = Math.max(1, Math.round(w * dpr))
        canvas.height = Math.max(1, Math.round(h * dpr))
        gl = canvas.getContext("webgl2")
        if (!gl) return void setFailed(true)
        state = await Promise.resolve(
          cfg.setup({ backend: "webgl2", gl, canvas, width: canvas.width, height: canvas.height })
        )
      } else {
        // 'canvas' — the effect owns the renderer and the backing store.
        state = await Promise.resolve(cfg.setup({ backend: "canvas", canvas, width: w, height: h }))
      }

      if (destroyed) {
        teardownState()
        return
      }

      // pauseWork subscribes element-less (loop keeps ticking; we gate work via
      // `onScreen`); pauseAll passes the element so the scheduler skips off-screen.
      unsubscribe = frameScheduler.subscribe(
        id,
        frameCallback,
        mode === "pauseAll" ? container : null,
        cfg.priority ?? "critical"
      )
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const was = onScreen
          onScreen = e.isIntersecting
          if (!was && onScreen && surface) {
            try {
              surface.reconfigure()
            } catch {
              /* ignore */
            }
          }
        }
      },
      { threshold: 0 }
    )
    io.observe(container)

    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(doResize, 150)
    }
    window.addEventListener("resize", onResize)

    unsubscribeLost = onDeviceLost(() => {
      if (destroyed || cfg.backend !== "webgpu") return
      unsubscribe?.()
      unsubscribe = null
      teardownState()
      surface?.dispose()
      surface = null
      device = null
      init().catch((err) => console.error(`[useGpuEffect] recovery '${id}' failed:`, err))
    })

    init().catch((err) => {
      console.error(`[useGpuEffect] init '${id}' failed:`, err)
      if (!destroyed) setFailed(true)
    })

    return () => {
      destroyed = true
      io.disconnect()
      window.removeEventListener("resize", onResize)
      if (resizeTimer) clearTimeout(resizeTimer)
      unsubscribe?.()
      unsubscribeLost?.()
      teardownState()
      surface?.dispose()
      // Release a WebGL2 context so it does not count against the browser's cap.
      if (cfg.backend === "webgl2" && gl) {
        try {
          gl.getExtension("WEBGL_lose_context")?.loseContext()
        } catch {
          /* ignore */
        }
      }
      // The shared WebGPU device is intentionally NOT destroyed here.
    }
    // Mount-once. Config is read through configRef; changing `backend` needs a remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (staticFallback || failed) {
    return <>{config.fallback ?? null}</>
  }

  return (
    <div
      ref={containerRef}
      className={config.className}
      style={config.style ?? { position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
      {config.overlay}
    </div>
  )
}
